import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, X, Image } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MediaUrlsInput = ({ urls, onChange }) => {
  const [newUrl, setNewUrl] = useState('');

  const addUrl = () => {
    const trimmed = newUrl.trim();
    if (!trimmed) return;
    onChange([...urls, trimmed]);
    setNewUrl('');
  };

  const removeUrl = (index) => {
    onChange(urls.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-3">
      <Label>Images / Videos</Label>
      <p className="text-xs text-maroon/50">Add multiple image or video URLs for the carousel</p>
      
      {urls.length > 0 && (
        <div className="space-y-2">
          {urls.map((url, index) => (
            <div key={index} className="flex items-center gap-2 bg-sand/30 border border-maroon/10 rounded-sm p-2">
              <img
                src={url}
                alt={`Media ${index + 1}`}
                className="w-12 h-12 object-cover rounded-sm border border-maroon/10 flex-shrink-0"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <span className="text-sm text-maroon/70 truncate flex-1">{url}</span>
              <button
                type="button"
                onClick={() => removeUrl(index)}
                className="p-1 text-coral hover:bg-coral/10 rounded-sm flex-shrink-0"
                data-testid={`remove-media-${index}`}
              >
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        <Input
          value={newUrl}
          onChange={(e) => setNewUrl(e.target.value)}
          placeholder="Paste image or video URL..."
          className="input-field flex-1"
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addUrl(); } }}
          data-testid="media-url-input"
        />
        <Button
          type="button"
          onClick={addUrl}
          className="bg-terracotta text-white hover:bg-terracotta/90 px-4"
          data-testid="add-media-url-btn"
        >
          <Plus size={16} className="mr-1" /> Add
        </Button>
      </div>

      {urls.length === 0 && (
        <div className="border-2 border-dashed border-maroon/15 rounded-sm p-6 text-center">
          <Image size={32} className="mx-auto text-maroon/20 mb-2" />
          <p className="text-sm text-maroon/40">No images added yet. Add at least one image URL above.</p>
        </div>
      )}
    </div>
  );
};

const DestinationsManager = () => {
  const [destinations, setDestinations] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingDest, setEditingDest] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    media: [],
    discount: '',
    valid_until: ''
  });

  const fetchDestinations = async () => {
    try {
      const response = await axios.get(`${API}/destinations`);
      setDestinations(response.data);
    } catch (error) {
      toast.error('Failed to fetch destinations');
    }
  };

  useEffect(() => {
    fetchDestinations();
  }, []);

  const resetForm = () => {
    setFormData({ name: '', description: '', media: [], discount: '', valid_until: '' });
    setEditingDest(null);
  };

  const openCreate = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const openEdit = (dest) => {
    const media = dest.media && dest.media.length > 0
      ? dest.media
      : dest.image_url ? [dest.image_url] : [];

    setFormData({
      name: dest.name || '',
      description: dest.description || '',
      media: media,
      discount: dest.discount || '',
      valid_until: dest.valid_until || ''
    });
    setEditingDest(dest);
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.media.length === 0) {
      toast.error('Please add at least one image');
      return;
    }

    const token = localStorage.getItem('token');
    const payload = {
      ...formData,
      image_url: formData.media[0],
    };

    try {
      if (editingDest) {
        await axios.put(`${API}/destinations/${editingDest.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Destination updated successfully');
      } else {
        await axios.post(`${API}/destinations`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Destination created successfully');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchDestinations();
    } catch (error) {
      toast.error(editingDest ? 'Failed to update destination' : 'Failed to create destination');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this destination?')) return;
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/destinations/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Destination deleted');
      fetchDestinations();
    } catch (error) {
      toast.error('Failed to delete destination');
    }
  };

  return (
    <div data-testid="destinations-manager">
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-3xl font-heading font-bold text-maroon">Hot Destinations</h2>
        <Button onClick={openCreate} className="btn-primary" data-testid="add-destination-btn">
          <Plus size={20} className="mr-2" />
          Add Destination
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {destinations.map((dest) => {
          const imageCount = (dest.media && dest.media.length > 0) ? dest.media.length : (dest.image_url ? 1 : 0);
          return (
            <div key={dest.id} className="card" data-testid="destination-item">
              <div className="relative">
                <img src={dest.image_url || (dest.media && dest.media[0])} alt={dest.name} className="w-full h-40 object-cover rounded-sm border border-maroon/10 mb-3" />
                {imageCount > 1 && (
                  <span className="absolute top-2 right-2 bg-maroon/80 text-cream text-xs px-1.5 py-0.5 rounded-sm font-bold">
                    {imageCount}
                  </span>
                )}
              </div>
              <div className="flex items-start justify-between mb-2">
                <h3 className="font-heading font-bold text-lg text-maroon">{dest.name}</h3>
                <span className="bg-terracotta text-white px-2 py-1 rounded-sm text-xs font-bold">{dest.discount}</span>
              </div>
              <p className="text-maroon/70 text-sm mb-3">{dest.description}</p>
              <div className="flex items-center justify-between">
                <p className="text-xs text-maroon/50">Until {dest.valid_until}</p>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => openEdit(dest)}
                    variant="outline"
                    size="sm"
                    className="border-maroon/20 text-maroon hover:bg-maroon/5"
                    data-testid="edit-destination-btn"
                  >
                    Edit
                  </Button>
                  <Button
                    onClick={() => handleDelete(dest.id)}
                    variant="destructive"
                    size="sm"
                    data-testid="delete-destination-btn"
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-maroon">
              {editingDest ? 'Edit Hot Destination' : 'Add Hot Destination'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="destination-form">
            <div className="space-y-2">
              <Label htmlFor="name">Destination Name *</Label>
              <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required className="input-field" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea id="description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} required rows={3} className="bg-white border-2 border-maroon/20 focus:border-maroon focus:ring-0 rounded-sm resize-none" />
            </div>

            <MediaUrlsInput
              urls={formData.media}
              onChange={(media) => setFormData({ ...formData, media })}
            />

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="discount">Discount *</Label>
                <Input id="discount" placeholder="e.g., 30% OFF" value={formData.discount} onChange={(e) => setFormData({ ...formData, discount: e.target.value })} required className="input-field" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="valid_until">Valid Until *</Label>
                <Input id="valid_until" placeholder="e.g., Dec 31, 2026" value={formData.valid_until} onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })} required className="input-field" />
              </div>
            </div>

            <Button type="submit" className="btn-primary w-full">
              {editingDest ? 'Update Destination' : 'Create Destination'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DestinationsManager;
